import React, { Component } from 'react';
import { withRouter } from '../navigation/withNavigation';
import { getAuthHeaders, urlMap } from '../../utils';
import { TextEditor } from '../dashboard/chapterPage/editor/TextEditor';
import CoachPanel from '../dashboard/chapterPage/Coach';
import ExportIcon from '../../components/ExportIcon';
import { Icon } from '../components/Icon';
import Footer from '../components/Footer';
import ActionButton from '../components/ActionButton';
import ShareDraftModal from './ShareDraftModal';
import { mdToHtml, htmlToMd } from '../../utils/markdown';

class DraftEditorPage extends Component {
  editorRef = React.createRef();

  state = {
    loading: true,
    saving: false,
    deleting: false,
    showCoachPanel: false,
    saveError: null,
    lastSavedAt: null,
    error: null,
    draftId: null,
    shareModalOpen: false,
    title: '',
    contentMd: '',
    // Coach state
    coachLoading: false,
    coachError: null,
    coachSending: false,
    coachTyping: false,
    selectedModel: null,
    coachMasterModelName: null,
    sessionId: null,
    chatMessages: [],
  };

  autoSaveTimer = null;
  lastSavedTitle = '';
  lastSavedContentMd = '';

  async componentDidMount() {
    // Make Enter split into <p> blocks (not zero-margin <div>s) so paragraph
    // gaps stay visible and match the \n\n paragraph-break markdown format.
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* unsupported */ }
    await this.loadDraft();
  }

  async componentDidUpdate(prevProps) {
    const prevId = prevProps.params?.draftId;
    const currId = this.props.params?.draftId;
    if (prevId !== currId) {
      await this.loadDraft();
    }
  }

  componentWillUnmount() {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    if (this.isDraftDirty()) {
      this.saveDraft({ suppressErrorToast: true });
    }
  }

  async loadDraft() {
    const draftId = this.props.params?.draftId;
    if (!draftId) {
      this.setState({ loading: false, error: 'Missing draft id.' });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const res = await fetch(urlMap.getDraft(draftId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to load draft (${res.status})`);

      const draft = await res.json();
      const loadedTitle = draft?.chapter_title || 'Untitled Draft';
      const loadedContent = draft?.chapter_content || '';

      this.lastSavedTitle = loadedTitle;
      this.lastSavedContentMd = loadedContent;

      this.setState({
        loading: false,
        draftId: draft?.id,
        title: loadedTitle,
        contentMd: loadedContent,
        saveError: null,
        lastSavedAt: null,
      }, () => {
        if (this.editorRef.current) {
          this.editorRef.current.innerHTML = mdToHtml(loadedContent);
        }
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: err?.message || 'Failed to load draft.',
      });
    }
  }

  onTitleChange = (e) => {
    this.setState({ title: e.target.value }, this.scheduleAutoSave);
  };

  onEditorInput = () => {
    const html = this.editorRef.current?.innerHTML || '';
    this.setState({ contentMd: htmlToMd(html) }, this.scheduleAutoSave);
  };

  onEditorKeyDown = () => {};

  onEditorPaste = () => {};

  onBackToHome = async () => {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    await this.saveDraft({ suppressErrorToast: true });
    this.props.navigate('/home');
  };

  onShowCoachPanel = async () => {
    this.setState({ showCoachPanel: true });
    await this.ensureSessionAndLoad();
  };

  onHideCoachPanel = () => {
    this.setState({ showCoachPanel: false });
  };

  onOpenShareModal = () => {
    this.setState({ shareModalOpen: true });
  };

  onCloseShareModal = () => {
    this.setState({ shareModalOpen: false });
  };

  // --- Coach Backend Methods ---
  async ensureSessionAndLoad() {
    const { draftId } = this.state;
    if (!draftId) return;

    this.setState({ coachLoading: true, coachError: null });

    let selectedModel = this.state.selectedModel;
    if (!selectedModel) {
      selectedModel = await this.fetchCoachMasterModel();
      if (!selectedModel) {
        this.setState({ coachLoading: false, coachError: "No master model configured" });
        return;
      }
    }

    const sessions = await this.fetchSessions(selectedModel, draftId);
    let sessionId = sessions?.[0]?.id || null;

    if (!sessionId) {
      const created = await this.createSession(selectedModel, draftId, "Draft Coach");
      sessionId = created?.id || null;
    }

    this.setState({ sessionId }, () => {
      if (sessionId) this.fetchMessages(sessionId);
      else this.setState({ coachLoading: false, coachError: "Could not create session" });
    });
  }

  async fetchCoachMasterModel() {
    try {
      const res = await fetch(urlMap.listLlms() + "/master", {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const model = data?.model;
      if (!model?.id) throw new Error("No master model configured");
      this.setState({ selectedModel: model.id, coachMasterModelName: model.name || model.id });
      return model.id;
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to load master model" });
      return null;
    }
  }

  async fetchSessions(selectedModelKey, draftId) {
    try {
      const url = new URL(urlMap.listChatSessions(), window.location.origin);
      if (selectedModelKey) url.searchParams.append("model", selectedModelKey);

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const sessions = await res.json();
      // Filter to only draft sessions
      return sessions.filter(s => s.content_type === 'draft' || s.draft_id);
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to load sessions" });
      return [];
    }
  }

  async createSession(modelKey, draftId, title = "Draft Coach") {
    try {
      const res = await fetch(urlMap.createDraftChatSession, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ llmId: modelKey, draftId, title }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to create session" });
      return null;
    }
  }

  async fetchMessages(sessionId) {
    try {
      const res = await fetch(urlMap.listChatMessages() + "?sessionId=" + sessionId, {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const visibleData = data.filter((m) => m.role !== "system");
      const bubbles = visibleData.map((m) => ({
        id: m.id,
        from: m.role === "user" ? "me" : "them",
        text: this.partsToPlainText(m.content),
      }));
      this.setState({ chatMessages: bubbles, coachLoading: false, coachError: null });
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to load messages", coachLoading: false });
    }
  }

  async sendUserMessage(sessionId, text) {
    const { selectedModel } = this.state;
    if (!selectedModel) {
      this.setState({ coachError: 'No master model configured' });
      return null;
    }

    try {
      const res = await fetch(urlMap.createChatMessage(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          sessionId,
          llmId: selectedModel,
          content: [{ type: 'text', text }],
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ coachError: err.message || 'Failed to send message' });
      return null;
    }
  }

  switchCoachSession = async (sessionId) => {
    this.setState({ sessionId, chatMessages: [], coachLoading: true, coachError: null });
    await this.fetchMessages(sessionId);
  };

  onCoachReset = async () => {
    const { draftId } = this.state;
    if (!draftId) return;

    let { selectedModel } = this.state;
    if (!selectedModel) {
      selectedModel = await this.fetchCoachMasterModel();
      if (!selectedModel) {
        this.setState({ coachError: "No master model configured" });
        return;
      }
    }

    const created = await this.createSession(selectedModel, draftId, "Draft Coach");
    const sessionId = created?.id || null;

    if (sessionId) {
      this.setState({ sessionId, chatMessages: [], coachLoading: true }, async () => {
        await this.fetchMessages(sessionId);
      });
    } else {
      this.setState({ coachError: "Failed to reset chat" });
    }
  };

  onCoachSend = async (text) => {
    const { sessionId } = this.state;
    if (!sessionId || !text.trim()) return;

    this.setState((s) => ({
      coachSending: true,
      coachTyping: true,
      chatMessages: [...s.chatMessages, { id: `local-${Date.now()}`, from: "me", text }],
    }));

    const run = await this.sendUserMessage(sessionId, text);
    if (!run?.runId) {
      this.setState({ coachSending: false, coachTyping: false });
      return;
    }

    await this.streamAssistantSSE(sessionId, run.runId);
  };

  async streamAssistantSSE(sessionId, runId) {
    const streamUrl = new URL(urlMap.streamRun(runId), window.location.origin);
    streamUrl.searchParams.set("sessionId", sessionId);

    const draftId = `assist-${runId}`;
    this.setState((s) => ({
      chatMessages: [...s.chatMessages, { id: draftId, from: "them", text: "" }],
    }));

    this.readerAbortController = new AbortController();

    try {
      const res = await fetch(streamUrl.toString(), {
        method: "GET",
        headers: { ...getAuthHeaders(), Accept: "text/event-stream" },
        signal: this.readerAbortController.signal,
      });

      if (!res.ok || !res.body) {
        this.updateAssistantBubble(draftId, "⚠️ Stream failed.");
        this.setState({ coachSending: false });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assembled = "";

      const feed = (chunk) => {
        buffer += chunk;
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";

        for (const frame of frames) {
          const lines = frame.split("\n");
          for (const line of lines) {
            const t = line.trim();
            if (!t.startsWith("data:")) continue;
            const json = t.slice(5).trim();

            try {
              const evt = JSON.parse(json);
              const kind = evt.event ?? evt.type;
              const delta = evt.delta ?? evt.payload?.delta;

              if (kind === "delta" && delta?.type === "text" && typeof delta.text === "string") {
                assembled += delta.text;
                this.updateAssistantBubble(draftId, assembled);
              } else if (kind === "done") return true;
            } catch {}
          }
        }
        return false;
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        let chunk = decoder.decode(value, { stream: true });
        let isTyping = true;

        if (chunk && isTyping) {
          this.setState({ coachTyping: false });
          isTyping = false;
        }
        const finished = feed(chunk);
        if (finished) break;
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        this.updateAssistantBubble(draftId, "⚠️ Stream error: " + (err?.message || "Unknown"));
      }
    } finally {
      this.setState({ coachSending: false, coachTyping: false });
    }
  }

  updateAssistantBubble = (id, text) => {
    this.setState((s) => ({
      chatMessages: s.chatMessages.map((m) =>
        m.id === id ? { ...m, text } : m
      ),
    }));
  };

  partsToPlainText = (parts) => {
    if (!parts) return '';
    if (Array.isArray(parts)) {
      return parts.map((p) => (typeof p === 'string' ? p : p.text || '')).join('');
    }
    return typeof parts === 'string' ? parts : '';
  };

  onExportDraft = async () => {
    const { draftId } = this.state;
    if (!draftId) return;

    try {
      const res = await fetch(urlMap.exportDraftPdf(draftId), {
        method: 'GET',
        headers: { ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!res.ok) {
        const payload = await res.text().catch(() => 'Unknown error');
        throw new Error(payload || `HTTP_${res.status}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.state.title || 'draft'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err?.message || 'Could not export draft.';
      alert(message);
    }
  };

  onDeleteDraft = async () => {
    const { draftId, deleting } = this.state;
    if (!draftId || deleting) return;

    const confirmed = window.confirm('Delete this draft? This cannot be undone.');
    if (!confirmed) return;

    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    this.setState({ deleting: true, error: null });
    try {
      const res = await fetch(urlMap.deleteDraft(draftId), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }

      this.props.navigate('/home');
    } catch (err) {
      const message = err?.message || 'Could not delete draft.';
      this.setState({ error: message });
      alert(message);
    } finally {
      this.setState({ deleting: false });
    }
  };

  isDraftDirty = () => {
    const { title, contentMd } = this.state;
    return title !== this.lastSavedTitle || contentMd !== this.lastSavedContentMd;
  };

  scheduleAutoSave = () => {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }

    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      this.saveDraft({ suppressErrorToast: true });
    }, 900);
  };

  saveDraft = async ({ suppressErrorToast = false } = {}) => {
    const { draftId, title, contentMd, saving } = this.state;
    if (!draftId || saving || !this.isDraftDirty()) return;

    this.setState({ saving: true, saveError: null });
    try {
      const res = await fetch(urlMap.updateDraft(draftId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          chapterTitle: title,
          chapterContent: contentMd,
        }),
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }

      this.lastSavedTitle = title;
      this.lastSavedContentMd = contentMd;
      this.setState({ lastSavedAt: new Date().toISOString() });
    } catch (err) {
      const message = err?.message || 'Could not autosave draft.';
      this.setState({ saveError: message });
      if (!suppressErrorToast) {
        alert(message);
      }
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const {
      loading,
      error,
      title,
      saving,
      deleting,
      showCoachPanel,
      saveError,
      lastSavedAt,
      draftId,
      shareModalOpen,
    } = this.state;

    const saveStateText = saving
      ? 'Autosaving...'
      : saveError
        ? 'Autosave failed'
        : lastSavedAt
          ? 'Saved'
          : '';

    if (loading) {
      return (
        <div className="home onboarding-writer-page onboarding-writer-page--loading">
          <h2>Loading draft editor...</h2>
        </div>
      );
    }

    return (
      <div className="home onboarding-writer-page">
        {error && (
          <div className="onboarding-alert onboarding-alert--spaced">
            {error}
          </div>
        )}

        <div className={`editor-root focus-lock writer-mode${showCoachPanel ? ' show-coach' : ''}`}>
          <div className="editor-shell">
            <div className="onboarding-editor-column draft-editor-main-column">
              <div className="onboarding-draft-title-row">
                <ActionButton
                  className="btn subtle pf-back draft-editor-back-arrow"
                  onClick={this.onBackToHome}
                  ariaLabel="Back to home"
                  title="Back to home"
                >
                  ←
                </ActionButton>
                <input
                  value={title}
                  onChange={this.onTitleChange}
                  placeholder="Draft title"
                  className="onboarding-title-input"
                />

                <div className="draft-title-row-actions">
                  <ExportIcon
                    onClick={this.onExportDraft}
                    title="Export as PDF"
                    className="draft-title-row-export-btn"
                  />

                  <button
                    type="button"
                    className="btn subtle draft-share-btn"
                    onClick={this.onOpenShareModal}
                    title="Share this draft"
                    aria-label="Share this draft"
                  >
                    <Icon.Share width="18" height="18" />
                  </button>

                  {!showCoachPanel && (
                    <ActionButton
                      className="btn subtle draft-show-coach-btn"
                      onClick={this.onShowCoachPanel}
                    >
                      Coach
                    </ActionButton>
                  )}

                  {!!saveStateText && (
                    <span className={`draft-editor-save-state${saveError ? ' is-error' : ''}`}>{saveStateText}</span>
                  )}
                </div>
              </div>

              <TextEditor
                editorRef={this.editorRef}
                isWriterMode={true}
                onInput={this.onEditorInput}
                onKeyDown={this.onEditorKeyDown}
                onPaste={this.onEditorPaste}
                topContent={null}
              />

              <div className="draft-editor-under-editor">
                <ActionButton
                  className="btn subtle draft-delete-btn"
                  onClick={this.onDeleteDraft}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Draft'}
                </ActionButton>
              </div>

              <Footer />
            </div>

            {showCoachPanel && (
              <CoachPanel
                draftId={this.state.draftId}
                messages={this.state.chatMessages}
                loading={this.state.coachLoading}
                error={this.state.coachError}
                sending={this.state.coachSending}
                typing={this.state.coachTyping}
                masterModelName={this.state.coachMasterModelName}
                onClose={this.onHideCoachPanel}
                onSend={this.onCoachSend}
                onReset={this.onCoachReset}
                onSwitchSession={this.switchCoachSession}
                currentSessionId={this.state.sessionId}
              />
            )}
          </div>
        </div>

        <ShareDraftModal
          isOpen={shareModalOpen}
          onClose={this.onCloseShareModal}
          draftId={draftId}
        />
      </div>
    );
  }
}

export default withRouter(DraftEditorPage);
